import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { useParams } from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { t } from "i18next";
import { IInventoryStatusData } from "../../../interfaces/IInventoryStatusData";
import { IPageProps } from "../../../interfaces/page-data";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";

const UpdateInventoryStatus: React.FunctionComponent<IPageProps> = () => {
  const toast = useRef<any>(null);
  const { inventoryStatusId } = useParams<any>();
  const [inventoryStatusData, setInventoryStatusData] = useState<IInventoryStatusData>();
  const [companyData, setCompanyData] = useState<ICompanyData[]>();
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (inventoryStatusId != null) {
      getInventoryStatusData(inventoryStatusId);
    } else if (inventoryStatusData?.inventoryStatusId) {
      getInventoryStatusData(inventoryStatusData.inventoryStatusId);
    }
  }, [inventoryStatusId]);

  const fetchData = async () => {
    const result = await HTTPService.GETCompanyNames();
    setCompanyData(result);
  };

  const getInventoryStatusData = (inventoryStatusId: number) => {
    HTTPService.GET(`/InventoryStatus/ById?inventoryStatusId=${inventoryStatusId}`)
      .then((res) => {
        if (!res.data) {
          return;
        }
        if (res.status === 200) {
          let tmp: IInventoryStatusData = res.data;
          setInventoryStatusData(tmp);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: res.error.message,
            life: 3000
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching application data:", error);
      });
  };

  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };


  const updateInventoryStatus = (formData) => {
    const updatedInventoryStatus = {
      inventoryStatusId: inventoryStatusId,
      inventoryStatusName: formData.inventoryStatusName,
      companyId: formData.companyId || userInfo?.companyId,
    };
    HTTPService.PUT(`/InventoryStatus/updateInventoryStatus`, updatedInventoryStatus)
      .then((res) => {
        if (res.status === 200) {
          toast.current?.show({
            severity: 'success',
            summary:  t('successful'),
            detail: t('inventorystatusupdate'),
            life: 3000
          });
          window.location.replace("/admin/inventory-status-management");
        } else if (res.status === 450) {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        }
      })
      .catch((error) => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: "There was an error updating the inventory status: " + error,
          life: 3000
        });
      });
  };

  const handleCancel = () => {
    window.location.replace("/admin/inventory-status-management/");
  };


  return (
    <>
    <Toast ref={toast} />
    <Form
      layout="vertical"
      onFinish={updateInventoryStatus}
      fields={[
        { name: ["inventoryStatusId"], value: inventoryStatusId || inventoryStatusData?.inventoryStatusId },
        { name: ["inventoryStatusName"], value: inventoryStatusData?.inventoryStatusName },
        { name: ["companyId"], value: inventoryStatusData?.companyId },
      ]}
    >
      <Card style={{ padding: "50px" }}>
        <h3>{t('update_inventory_status')}</h3>
        {userInfo?.role === UserRole.AltisAdmin ? (
          <Form.Item
            name='companyId'
            label={t('Select company')}
            rules={[
              {
                required: true,
                message: 'Please select a company!',
              },
            ]}>
            <Select showSearch placeholder={t('company_name')} style={{ marginBottom: '6px' }} filterOption={filterOption}>
              {companyData?.map((company) => (
                <Select.Option key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : null}
        <Row gutter={16}>
          <Col xs={24} sm={24} md={16} lg={24} xl={24}>
            <Form.Item
              name="inventoryStatusName"
              label={t('inventory_status_name')}
              rules={[
                {
                  required: true,
                  message: t('please_input_your_inventory_status_name'),
                  whitespace: true,
                },
              ]}
            >
              <Input
                style={{ borderRadius: 0 }}
                placeholder={t('inventory_status_name')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Row gutter={[4, 4]} justify="end" align="middle">
            <Col xs={12} sm={4}>
              <Button
                block
                onClick={handleCancel}
                type="primary"
                style={{
                  height: 64,
                  backgroundColor: 'transparent',
                  color: '#1890ff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 10px',
                  border: 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                  e.currentTarget.style.color = '#40a9ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.color = '#1890ff';
                }}
              >
                <CloseOutlined style={{ fontSize: 24 }} />
                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                  {t('cancel')}
                </span>
              </Button>
            </Col>
            <Col xs={12} sm={4}>
              <Button
                block
                htmlType="submit"
                type="primary"
                style={{
                  height: 64,
                  backgroundColor: 'transparent',
                  color: '#1890ff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 10px',
                  border: 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                  e.currentTarget.style.color = '#40a9ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.color = '#1890ff';
                }}
              >
                <SyncOutlined style={{ fontSize: 24 }} />
                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                  {t('update')}
                </span>
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Card>
    </Form>
    </>

  );
};

export default UpdateInventoryStatus;